import * as Models from '../models/models';
import {QuoteWizardViewModel} from './quoteWizard.viewmodel';
import {ClientStateContext, IClientStateContext} from './clientstatecontext.model';
import {NavigationContext} from '../core/services/wizardNavigationService/navigationcontext.model';
import {QuoteWizardQuestionResponseGraph} from './models/quoteWizardQuestionResponseGraph.model';
import * as Repositories from '../repositories/repositories';
import {ClientStateContextRepository} from './repositories/clientStateContext.repository';
import ResponseType = Models.Constants.ResponseType;
import _ = require('lodash');
import { IUserProfile } from '../models/models';
import { WizardSavedState } from '../core/services/wizardNavigationService/wizardSavedState';
import { SerializedWizardFocus } from '../core/services/wizardNavigationService/serializedWizardFocus';

export class QuoteWizardGateway {
    private incompleteSubmissionPromise: ng.IPromise<any>;

    public static $inject = [
        'clientStateContextRepository',
        'submissionRequestRepository',
        'submissionRepository',
        'policysummaryRepository',
        '$q',
        'userRepository'];

    constructor(private clientStateContextRepository: ClientStateContextRepository,
                private submissionRequestRepository: Repositories.SubmissionRequestRepository,
                private submissionRepository: Repositories.SubmissionRepository,
                private policySummaryRepository: Repositories.PolicySummaryRepository,
                private $q: ng.IQService,
                private userRepository: Repositories.UserRepository) {
    }

    public saveIncompleteSubmission(viewModel: QuoteWizardViewModel): ng.IPromise<any> {
        const context = viewModel.clientStateContext;
        context.submissionRequest.questionResponses = this.mapViewModelQuestionResponseGraphToResponses(viewModel, context.id);
        if (angular.isDefined(this.incompleteSubmissionPromise)) {
            this.incompleteSubmissionPromise = this.incompleteSubmissionPromise.then(() => this.insertOrUpdateClientStateContext(context));
        } else {
            this.incompleteSubmissionPromise = this.insertOrUpdateClientStateContext(context);
        }
        return this.incompleteSubmissionPromise;
    }

    private insertOrUpdateClientStateContext(context: IClientStateContext): ng.IPromise<any> {
        if (angular.isUndefined(context.id)) {
            // insert
            context.contextVersion = '1.1';
            context.submissionRequest.primaryContactId = context.userId;
            return this.clientStateContextRepository.post(context);
        }
        return this.clientStateContextRepository.put(context);
    }

    public loadViewModel(userId: string): ng.IPromise<QuoteWizardViewModel> {
        const viewModel = new QuoteWizardViewModel();

        if (userId) {
            const userPromise = this.userRepository.getById(userId)
            .then((user: Models.IUserProfile) => {
                viewModel.firstName = user.firstName;
                viewModel.lastName = user.lastName;
                viewModel.emailAddress = user.emailAddress;
            });

            const clientStateContextPromise = this.loadClientStateContext(userId)
            .then((clientStateContext: ClientStateContext) => {

                if(clientStateContext == null) {
                    clientStateContext = this.initializeDefaultClientStateContext();
                    clientStateContext.userId = userId;
                    //if there is a user record and not a client state context then advance the wizard to the step after the data is collected to create a user
                    clientStateContext.navigationContext.wizardSavedState = new WizardSavedState();
                    clientStateContext.navigationContext.wizardSavedState.history = new Array<SerializedWizardFocus>();

                    let startNode = new SerializedWizardFocus();
                    startNode.nodeStateName = 'shellFrame.quoteWizardFrame.wizard.contactInfoBumper',
                    startNode.nodeSubjectIndex = null;

                    let nameNode = new SerializedWizardFocus();
                    nameNode.nodeStateName = 'shellFrame.quoteWizardFrame.wizard.fullName',
                    nameNode.nodeSubjectIndex = 0;

                    let emailNode = new SerializedWizardFocus();
                    emailNode.nodeStateName = 'shellFrame.quoteWizardFrame.wizard.email',
                    emailNode.nodeSubjectIndex = 0;
                    
                    let phoneNumberNode = new SerializedWizardFocus();
                    phoneNumberNode.nodeStateName = 'shellFrame.quoteWizardFrame.wizard.phoneNumber';
                    phoneNumberNode.nodeSubjectIndex = 0;

                    clientStateContext.navigationContext.wizardSavedState.history.push(startNode, nameNode, emailNode, phoneNumberNode);

                    viewModel.clientStateContext = clientStateContext;
                }
                else {
                    viewModel.clientStateContext = clientStateContext;
                    viewModel.questionResponseGraph = this.mapResponsesToViewModelQuestionResponseGraph(viewModel.clientStateContext.submissionRequest.questionResponses);
                    this.hydrateViewModelFromLoadedContext(viewModel);
                }
                return this.$q.when(viewModel);
            });

            return this.$q.all([userPromise, clientStateContextPromise]).then(() => this.$q.when(viewModel));
        }
        else {
            viewModel.clientStateContext = this.initializeDefaultClientStateContext();
            return this.$q.when(viewModel);
        }
    }

    public saveAndQuoteSubmissionRequest(viewModel: QuoteWizardViewModel): ng.IPromise<any> {
        // make sure to only submit the submission once all the incomplete submission promises are finished
        return this.incompleteSubmissionPromise.then(() => {
            const submissionRequest = viewModel.clientStateContext.submissionRequest;
            submissionRequest.questionResponses = this.mapViewModelQuestionResponseGraphToResponses(viewModel, submissionRequest.id);
            // temporarily, we will fire and forget the finish line endpoint, so the user doesn't have to wait
            // so fire that here, and ignore the promise instead of returning it
            // return this.submissionRequestRepository.post(submissionRequest);
            this.submissionRequestRepository.post(submissionRequest);
        });
    }

    public getSubmissionById(submissionId: string): ng.IPromise<any> {
        return this.submissionRepository.getById(submissionId);
    }

    public getPolicySummaryBySubmissionId(submissionId: string): ng.IPromise<any> {
        return this.policySummaryRepository.getAllLinesPolicyBySubmissionId(submissionId);
    }

    private hydrateViewModelFromLoadedContext(viewModel: QuoteWizardViewModel) {
        // location "has liquor sales" question and location "own building" question
        _.each(viewModel.clientStateContext.submissionRequest.locations, (location) => {
            if (location.salesLiquor > 0) {
                location.haveLiquorSales = true;
            } else if (location.salesLiquor === 0) {
                location.haveLiquorSales = false;
            }
            if (location.buildingAmount > 0) {
                location.ownBuilding = true;
            } else if (location.buildingAmount === 0) {
                location.ownBuilding = false;
            }
        });
    }

    private mapViewModelQuestionResponseGraphToResponses(viewModel: QuoteWizardViewModel, submissionId: string): Models.IQuestionResponse[] {
        const results: Models.IQuestionResponse[] = [];
        const wizardResponseGraph = viewModel.questionResponseGraph;
        for (const key of Object.keys(wizardResponseGraph)) {
            const specificResponse: any = wizardResponseGraph[key];
            const resultingResponse = new Models.QuestionResponse();
            if (angular.isUndefined(specificResponse.response)) {
                // do not send undefined responses to the server, wait for them to be filled out with an actual response
                continue;
            }
            resultingResponse.questionId = specificResponse.questionId;
            resultingResponse.submissionId = submissionId;
            // switch on the response type so we can set the appropriate property for the response
            const responseType: ResponseType = specificResponse.responseType;
            switch (responseType) {
                case ResponseType.Boolean:
                    resultingResponse.responseBoolean = specificResponse.response;
                    break;
                case ResponseType.Integer:
                    resultingResponse.responseInteger = specificResponse.response;
                    break;
                case ResponseType.ShortText:
                    resultingResponse.responseText = specificResponse.response;
                    break;
                case ResponseType.ListCodeValue:
                    resultingResponse.responseCodeCId = specificResponse.response;
                    break;
            }
            results.push(resultingResponse);
        }
        return results;
    }

    private mapResponsesToViewModelQuestionResponseGraph(responses: Models.IQuestionResponse[]): QuoteWizardQuestionResponseGraph {
        const responseGraph: QuoteWizardQuestionResponseGraph = new QuoteWizardQuestionResponseGraph();
        if (angular.isUndefined(responses)) {
            return responseGraph;
        }
        for (const response of responses) {
            for (const key of Object.keys(responseGraph)) {
                const specificResponse: any = responseGraph[key];
                if (specificResponse.questionId !== response.questionId) {
                    continue;
                }
                // switch on the response type so we can set from the appropriate property for the response
                const responseType: ResponseType = specificResponse.responseType;
                switch (responseType) {
                    case ResponseType.Boolean:
                        specificResponse.response = response.responseBoolean;
                        break;
                    case ResponseType.Integer:
                        specificResponse.response = response.responseInteger;
                        break;
                    case ResponseType.ShortText:
                        specificResponse.response = response.responseText;
                        break;
                    case ResponseType.ListCodeValue:
                        // for some reason, this value comes from the api as a string
                        specificResponse.response = _.parseInt(response.responseCodeCId.toString());
                        break;
                }
            }
        }
        return responseGraph;
    }

    private loadClientStateContext(userId: string): ng.IPromise<ClientStateContext> {
        return this.clientStateContextRepository.getClientStateContextByUserId(userId);
    }

    private initializeDefaultClientStateContext(){
        let clientStateContext = new ClientStateContext();
        clientStateContext.navigationContext = new NavigationContext();
        clientStateContext.submissionRequest = new Models.SubmissionRequest();
        clientStateContext.submissionRequest.mailingAddress = new Models.Address();
        clientStateContext.submissionRequest.locations = [];

        return clientStateContext;
    }
    public getUserProfile(): ng.IPromise<IUserProfile> {
        return this.userRepository.getUserProfile().then((userAccount: IUserProfile) => userAccount);
    }
}